import { DestroyRef, Injectable } from '@angular/core';
import { DiscussableDTO } from './api/discussable';
import { API, LinkedEntityDTO, PostDTO, TopicDTO, TopicFullDTO, UserDTO } from './api/api';
import { BehaviorSubject } from 'rxjs';
import { UserService } from './user.service';
import { ToasterService, ToastType } from './toaster/toaster.service';
import { ModalService } from './gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { PermissionService } from './permission.service';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

export interface GroupedTopics {
  element: any;
  topics: TopicDTO[];
  type: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class DiscussionService {
  public DISCUSSABLES = DiscussableDTO;

  public discussableKey: BehaviorSubject<LinkedEntityDTO | null>;
  keyArr: LinkedEntityDTO[];
  groupedDiscussionList: GroupedTopics[];
  selectedGroup: GroupedTopics;
  discussions: BehaviorSubject<TopicDTO[] | null>;
  selectedTopic: TopicFullDTO;
  showDiscussionSideBar: boolean;
  discussableElements: any;
  newTopic: boolean;
  cameFromSubTopic: boolean;
  cameFromSubTopicMemory: boolean;
  loading: boolean;
  showRibbon: boolean;
  isSingleDiscussionTopic: boolean;
  createdTopicID: number;
  animationCompleted: boolean;
  ribbonClicked: boolean;
  inputChanged: boolean;
  modalOpen: boolean;
  debounceTimer: ReturnType<typeof setTimeout> = null;

  constructor(
    private api: API,
    private userService: UserService,
    private toastHandler: ToasterService,
    private modalService: ModalService,
    private translate: TranslateService,
    private destroyRef: DestroyRef,
    private router: Router,
    public permissionService: PermissionService
  ) {
    this.keyArr = [];
    this.discussions = new BehaviorSubject<TopicDTO[]>(null);
    this.showDiscussionSideBar = false;
    this.groupedDiscussionList = [];
    this.discussableElements = [];
    this.cameFromSubTopic = false;
    this.cameFromSubTopicMemory = false;
    this.loading = true;
    this.showRibbon = false;
    this.isSingleDiscussionTopic = false;
    this.createdTopicID = -1;
    this.animationCompleted = false;
    this.ribbonClicked = false;
    this.modalOpen = false;
    this.closeNewTopic(); // newTopic and inputChanged = false
    this.loadShowRibbon();
    this.resub();
  }

  ribbonClick() {
    this.ribbonClicked = true;
    setTimeout(() => {
      this.ribbonClicked = false;
    }, 500);
  }

  modalOpenClick() {
    this.modalOpen = true;
    setTimeout(() => {
      this.modalOpen = false;
    }, 500);
  }

  saveShowRibbon(): void {
    this.userService.setUserSetting('showRibbon', this.showRibbon.toString()).then();
  }

  loadShowRibbon() {
    if (this.permissionService.hasPermission(this.permissionService.Permission.discussion_list)) {
      if (this.router.url.includes('admin')) {
        this.showRibbon = false; // TODO unauthorized user admin panel
      } else {
        toObservable(this.userService.currentUserSettings).subscribe(userSettings => {
          if (userSettings) {
            const e = userSettings.showRibbon;
            if (e != null) {
              this.showRibbon = e === 'true';
            }
          }
        });
      }
    } else {
      this.showRibbon = false;
    }
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshDiscussionTopics().then();
    }, 2000);
  }

  refreshDiscussionTopics(refresh?: boolean) {
    return new Promise(resolve => {
      // debugger; TODO check double load on cards
      if (this.permissionService.hasPermission(this.permissionService.Permission.discussion_list)) {
        this.api
          .shared_Discussion_retrieveTopicsWithOptions({ linkedEntities: this.keyArr })
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe((data: TopicDTO[]) => {
            if (!refresh) {
              if (this.discussions && data && data.length > 0) {
                this.discussions.next(data);
              }
              this.unsubKeys();
              this.resub();
            }
            this.buildDiscussableGroups(data).then(() => {
              resolve(true);
            });
          });
      }
    });
  }

  createTopic(post: TopicDTO) {
    return new Promise(resolve => {
      if (this.permissionService.hasPermission(this.permissionService.Permission.discussion_create)) {
        this.api
          .shared_Discussion_createTopic(this.selectedGroup.type, this.selectedGroup.element.id, post)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe((data: TopicDTO) => {
            this.createdTopicID = data.id;
            this.requestDebounced();
            this.newTopic = false;
            resolve(true);
          });
      }
    });
  }

  selectDiscussableGroup(elementId: number) {
    for (const group of this.groupedDiscussionList) {
      if (group.element.id === elementId) {
        this.selectedGroup = group;
      }
    }
  }

  inputChangedModal() {
    return new Promise(resolve => {
      if (!this.modalOpen) {
        this.modalOpen = true;
        this.modalService
          .confirm({
            title: this.translate.instant('lang.globals.deleteTopic'),
            message: this.translate.instant('lang.globals.deleteTopicInput'),
            yes: this.translate.instant('lang.globals.yes'),
            no: this.translate.instant('lang.globals.no'),
          })
          .then((result: boolean) => {
            this.modalOpenClick();
            resolve(result);
          });
      }
    });
  }

  closeSideBar() {
    if (this.inputChanged) {
      this.inputChangedModal().then((result: boolean) => {
        if (result) {
          this.closeSide();
          this.closeNewTopic();
          this.selectedGroup = null;
          this.cameFromSubTopicMemory = false;
          this.cameFromSubTopic = false;
        }
      });
    } else {
      this.newTopic = false;
      this.selectedGroup = null;
      this.cameFromSubTopicMemory = false;
      this.cameFromSubTopic = false;
      this.closeSide();
    }
  }

  openSideBar() {
    this.showDiscussionSideBar = true;
  }

  addNewTopic() {
    this.newTopic = true;
  }

  closeNewTopic() {
    this.newTopic = false;
    this.inputChanged = false;
  }

  toggleShowRibbon($event: MouseEvent) {
    if ($event) {
      $event.stopPropagation();
    }
    this.showRibbon = !this.showRibbon;
    this.saveShowRibbon();
  }

  buildDiscussableGroups(data: TopicDTO[]) {
    return new Promise(resolve => {
      const groups: GroupedTopics[] = [];
      let singleGroup: GroupedTopics = {
        element: null,
        topics: [],
        type: null,
      };
      for (const element of this.discussableElements) {
        singleGroup.element = element;
        for (const type of this.keyArr) {
          if (element.id === type.id) {
            singleGroup.type = type.type;
          }
        }
        for (const key of data) {
          if (singleGroup.type === key.entity.type) {
            if (element.id === key.entity.id) {
              singleGroup.topics.push(key);
            }
          }
        }
        let found = false;
        for (const p of groups) {
          if (p.element.id === singleGroup.element.id) {
            found = true;
          }
        }
        if (found === false) {
          groups.push(singleGroup);
        } else {
          found = false;
        }
        singleGroup = {
          element: null,
          topics: [],
          type: null,
        };
      }
      this.groupedDiscussionList = groups;
      this.loading = false;
      resolve(true);
    });
  }

  addDiscussableKey(discussableKey: LinkedEntityDTO): void {
    if (discussableKey.id) {
      this.discussableKey.next(discussableKey);
    }
  }

  sendReply(reply: string, postID: number, isTopic: boolean, mentionUsers?: UserDTO[]): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      if (reply.length > 0 && reply.trim().length > 0) {
        const temp = new PostDTO();
        temp.text = reply;
        if (mentionUsers && mentionUsers.length > 0) {
          temp.mentions = mentionUsers;
        }
        // TODO temp.mentions = parseTextForMentions(this.reply);
        if (this.permissionService.hasPermission(this.permissionService.Permission.discussion_participate)) {
          if (isTopic) {
            this.api
              .shared_Discussion_createTopicPost(postID, temp)
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe(
                () => {
                  res(true);
                },
                error => {
                  this.toastHandler.toast(ToastType.ERROR, error);
                  rej();
                }
              );
          } else {
            this.api
              .shared_Discussion_createAnswerPost(postID, temp)
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe(
                () => {
                  res(true);
                },
                error => {
                  this.toastHandler.toast(ToastType.ERROR, error);
                  rej();
                }
              );
          }
        }
      }
    });
  }

  setSolution(topicID: number, postID: number, setDone: boolean) {
    if (!setDone) {
      this.api
        .shared_Discussion_unsetSolution(topicID)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe(() => {});
    } else {
      this.api
        .shared_Discussion_setSolution(topicID, postID)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe(() => {});
    }
  }

  private closeSide() {
    this.showDiscussionSideBar = false;
  }

  private unsubKeys() {
    if (this.discussableKey) {
      try {
        this.discussableKey.unsubscribe();
        this.discussableKey = null;
        this.resub();
      } catch (err) {
        throw new Error(err);
      }
    }
  }

  private resub() {
    this.discussableKey = new BehaviorSubject<LinkedEntityDTO>(null);
    this.discussableKey.subscribe(keys => {
      if (keys?.id) {
        this.keyArr.push(keys);
        this.requestDebounced();
      }
    });
  }
}
